import { makeStyles } from '@mui/styles';
import Banner from 'apollo-react/components/Banner';
import Grid from 'apollo-react/components/Grid';
import Loader from 'apollo-react/components/Loader';
import _ from 'lodash';
import qs from 'query-string';
import React, { useEffect, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import {
  CERTIFICATION_CONTENT_DISPLAY,
  COURSE_ENTITY_NAME,
  COURSE_FORM_KEY,
  DOCUMENT_ENTITY_NAME,
  DOCUMENT_FORM_KEY,
} from './constants';
import Header from './Header';
import LeftPanel from './LeftPanel';
import Message from './Message';
import MiddlePanel from './MiddlePanel';
import {
  checkClassRoomTrainingRequired,
  clearCertificationData,
  loadContent,
} from '../../actions/CertificationActions';
import { clearDocumentViewer } from '../../actions/DocumentViewerActions';
import { clearPanelLayout } from '../../actions/PanelLayoutActions';
import { clearWorkflow } from '../../actions/WorkflowActions';
import PanelLayout from '../../components/general/PanelLayout';
import Layout from '../../components/Layout';
import withSubmitFormDialog from '../../components/withSubmitFormDialog';
import { PANEL_CONFIGURATIONS } from '../../constants/PanelLayoutConstants';
import * as Urls from '../../constants/Urls';
import { useWindowDimensions } from '../../custom-hooks/useWindowDimensions';
import { clearFormDesigner } from '../../form-designer/actions/form-manager.actions';
import DetailForm from '../../form-designer/components/DetailForm';
import FormFooter from '../../form-designer/components/FormFooter';
import { FORM_KEYS } from '../../form-designer/constants/form-manager.constants';
import { FormContext } from '../../form-designer/context';
import {
  hasForbiddenPermissionError,
  hasWarningsOrErrors,
  isFormLoading,
} from '../../form-designer/selectors/common.selectors';
import { clearValidationStorage } from '../../form-designer/utils/validationStorage.utils';
import { getAppSettingsByKey } from '../../selectors/AppConfigsSelectors';
import { getIsWorkflowRequestPending } from '../../selectors/WorkflowSelectors';
import { checkTrue } from '../../utils/commonUtils';
import AttachmentPreviewDialog from '../EntityDisplay/AttachmentPreviewDialog';

const getPanelsProps = (preRequisiteStatus, isMobileDevice) => ({
  middlePanel: {
    [PANEL_CONFIGURATIONS.LEFT_COLLAPSED]: {
      widthPercent: preRequisiteStatus === 'Not Complete' ? 100 : 50,
    },
  },
  rightPanel: {
    [PANEL_CONFIGURATIONS.LEFT_COLLAPSED]: {
      widthPercent: isMobileDevice ? 0 : preRequisiteStatus === 'Not Complete' ? 0 : 50,
    },
    isHideScroll: true,
  },
});

const useStyles = makeStyles({
  panelContainer: {
    height: 'calc(100vh - 178px)',
  },
  mobileMiddlePanel: {
    '& #formManager > div > div > div[class*="MuiAccordionDetails-root"]': {
      paddingRight: 0,
      paddingLeft: 0,
      paddingBottom: 0,
    },
  },
  loaderContainer: {
    height: 'calc(100vh - 64px)',
    position: 'relative',
  },
});

const CertificationPage = ({ location }) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { search } = useLocation();
  const isStepFormLoading = useSelector(isFormLoading(FORM_KEYS.STEP));
  const isDetailFormLoading = useSelector(isFormLoading(FORM_KEYS.DETAIL));
  const certificationData = useSelector((state) => state.certification);
  const isWorkflowRequestPending = useSelector(getIsWorkflowRequestPending);
  const hasPermissionError = useSelector(hasForbiddenPermissionError);
  const enableTrainingSessionSignUp = useSelector(
    getAppSettingsByKey('TrainingManagement/EnableTrainingSessionSignUp')
  );
  const contentPanelSide = useSelector(
    getAppSettingsByKey('SmartSolve/CertificationContentDisplay')
  );
  const hasWarnings = useSelector(hasWarningsOrErrors);
  const isSubmitInitiated = useSelector((state) => state.certification.submitInitiated);

  const [showMessage, setShowMessage] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);

  const { isMobileDevice } = useWindowDimensions();

  const {
    trainerSignoff,
    trainingProgress,
    actorClass,
    actorId,
    contentType,
    prerequisiteStatus,
    reqId,
    rpgId,
  } = qs.parse(location.search);

  const isDocument = contentType === 'DOCUMENT';
  const formKey = isDocument ? DOCUMENT_FORM_KEY : COURSE_FORM_KEY;
  const entityName = isDocument ? DOCUMENT_ENTITY_NAME : COURSE_ENTITY_NAME;
  const isTrainerSignOff = trainerSignoff === '1';
  const {
    classRoomTrainingIsRequired,
    message,
    preRequisiteStatus,
    preRequisites,
    preRequisiteIsRequired,
  } = certificationData;
  const isCheckTrainingRequired = !isTrainerSignOff && trainingProgress === '2';
  const checkPreRequisiteIsRequired = ['Complete', 'Not Complete'].includes(prerequisiteStatus);
  const shouldLoadCertification = !isCheckTrainingRequired || classRoomTrainingIsRequired === false;
  const panelsProps = getPanelsProps(preRequisiteStatus, isMobileDevice);

  useEffect(() => {
    dispatch(
      loadContent({
        contentType,
        reqId,
        shouldLoadCertification,
        isTrainerSignOff,
        checkPreRequisiteIsRequired,
        actorId,
        rpgId,
        actorClass,
        entityType: entityName,
        formKey,
      })
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldLoadCertification, location]);

  useEffect(() => {
    if (isCheckTrainingRequired) {
      dispatch(checkClassRoomTrainingRequired({ trainingProgress, actorId, rpgId }));
    }

    return () => {
      batch(() => {
        dispatch(clearFormDesigner(FORM_KEYS.STEP));
        dispatch(clearFormDesigner(FORM_KEYS.DETAIL));
        dispatch(clearCertificationData());
        dispatch(clearDocumentViewer());
        dispatch(clearPanelLayout());
        dispatch(clearWorkflow());
        clearValidationStorage();
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    if (classRoomTrainingIsRequired && checkTrue(enableTrainingSessionSignUp)) {
      const trainingSessionQuery =
        '&entityType=TRN_TRAINING_SESSION&formKey=TRN_TRAINING_SESSION_AVAILABLE_FOR_SIGNUP';
      history.replace(`${Urls.TRAININGS}${search}${trainingSessionQuery}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableTrainingSessionSignUp, classRoomTrainingIsRequired]);

  useEffect(() => {
    setShowMessage(!!message);
  }, [message]);

  const isLoading = isCheckTrainingRequired && _.isNil(classRoomTrainingIsRequired);
  const invertPanels = contentPanelSide?.toUpperCase() !== CERTIFICATION_CONTENT_DISPLAY;

  return (
    <Layout>
      {isLoading ? (
        <div className={classes.loaderContainer}>
          <Loader isInner />
        </div>
      ) : (
        <Grid container>
          <Grid item xs={12}>
            <Banner
              variant="warning"
              open={showMessage}
              message={message}
              onClick={() => setShowMessage(false)}
            />
            <Header
              reqId={reqId}
              isDocument={isDocument}
              setHeaderHeight={(h) => setHeaderHeight(h)}
            />
            <div
              style={{
                height: isMobileDevice
                  ? `calc(100vh - ${headerHeight + 100}px)`
                  : 'calc(100vh - 178px)',
              }}
            >
              {preRequisiteIsRequired ? (
                <PanelLayout
                  leftPanelDisabled={isMobileDevice ? true : !(isSubmitInitiated && hasWarnings)}
                  rightPanelDisabled={isMobileDevice ? true : isTrainerSignOff}
                  leftPanelChildren={isMobileDevice ? null : <LeftPanel />}
                  middlePanelChildren={
                    <>
                      <Message message={message} preRequisiteStatus={preRequisiteStatus} />
                      <MiddlePanel
                        preRequisites={preRequisites}
                        isStepFormLoading={isStepFormLoading}
                      />
                    </>
                  }
                  panelsProps={panelsProps}
                  invertPanels={invertPanels}
                />
              ) : (
                <>
                  <Message message={message} preRequisiteStatus={preRequisiteStatus} />
                  {shouldLoadCertification && (
                    <PanelLayout
                      leftPanelDisabled={
                        isMobileDevice ? true : !(isSubmitInitiated && hasWarnings)
                      }
                      rightPanelDisabled={isMobileDevice ? true : isTrainerSignOff}
                      leftPanelChildren={isMobileDevice ? null : <LeftPanel />}
                      middlePanelChildren={
                        isMobileDevice ? (
                          <>
                            <div className={classes.mobileMiddlePanel}>
                              <MiddlePanel
                                preRequisites={preRequisites}
                                isStepFormLoading={isStepFormLoading}
                              />
                            </div>
                            <DetailForm
                              isLoading={isDetailFormLoading}
                              isTrainerSignOff={isTrainerSignOff}
                            />
                          </>
                        ) : (
                          <MiddlePanel
                            preRequisites={preRequisites}
                            isStepFormLoading={isStepFormLoading}
                          />
                        )
                      }
                      middlePanelFormFooter={
                        <FormContext.Provider value={FORM_KEYS.STEP}>
                          {!isStepFormLoading && !hasPermissionError && <FormFooter />}
                        </FormContext.Provider>
                      }
                      rightPanelChildren={
                        isMobileDevice ? null : (
                          <DetailForm
                            isLoading={isDetailFormLoading}
                            isTrainerSignOff={isTrainerSignOff}
                          />
                        )
                      }
                      panelsProps={panelsProps}
                      invertPanels={invertPanels}
                    />
                  )}
                </>
              )}
            </div>
            {isWorkflowRequestPending && <Loader isInner />}
            <AttachmentPreviewDialog />
          </Grid>
        </Grid>
      )}
    </Layout>
  );
};

export default withSubmitFormDialog(CertificationPage);
